// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  Inject,
  Input,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Components } from 'fcis-widget/dist/types/components';
import { IFcisWidgetParameters } from 'fcis-widget/dist/types/interfaces/IInitializationConfig';
import { IOneCalcResponseV2 } from 'fcis-widget/dist/types/interfaces/IOneCalcResponseV2';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { SelectablesResponseVehicleComponent, VehicleComponent } from 'src/app/api/owcc/models';
import { Vehicle } from 'src/app/api/owcc/models/vehicle';
import { appInject } from 'src/app/app-injector';
import { getScriptUrl } from 'src/app/core/config/url.config';
import { LoggerService } from 'src/app/core/services/logger/logger.service';
import { selectFcEnvironment } from 'src/app/shared/financing/fcis.util';
import { FinanceService } from 'src/app/shared/financing/finance.service';
import {
  RatesAndSelectionInfo,
  selectConfigurationSelectableComponentsOrUndefined,
  selectConfigurationVehicle,
  selectSelectedComponents,
  selectShowRatesIsActiveAndSelectablesHasChanged
} from 'src/app/store/common/common.selectors';
import {
  financingFetchFcisVersion,
  financingUpdateFcisCalculationResult,
  financingUpdateIsFcisInitialized,
  financingUpdateIsFcisRateConfirmed,
  financingUpdateIsFcisResultAvailable
} from 'src/app/store/financing/financing.actions';
import { selectIsFcisInitialized } from 'src/app/store/financing/financing.selectors';
import { selectOnlineOrFinancialCode } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { ScriptParams } from 'src/app/utils/functions/load-external-script';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { VehicleComponentCollection } from 'src/app/utils/models/vehicle-component-collection.interface';
import { WINDOW } from 'src/app/utils/window.token';

import { ScriptLoaderComponent } from '../../script-loader/script-loader.component';
import { ShadowDomWrapperComponent } from '../../shadow-dom-wrapper/shadow-dom-wrapper.component';

import { FinanceCalculatorEnvironment } from './finance-calculator-environment.enum';

// an OnlineCode might be available or not
export type OptionalOnlineOrFinancialCode = string | undefined;

@Component({
  selector: 'cc-finance-calculator',
  templateUrl: './finance-calculator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ScriptLoaderComponent, ShadowDomWrapperComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class FinanceCalculatorComponent implements AfterViewInit {
  @Input() public isWidgetVisible: boolean;
  @ViewChild('fcisWidget') private readonly fcisWidget!: ElementRef<Components.FcisWidget>;

  public readonly scriptParams: ScriptParams = { scriptUrl: getScriptUrl('fcis'), isModule: true };

  public constructor(
    private readonly loggerService: LoggerService,
    private readonly financeService: FinanceService,
    private readonly store: Store,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  private get nativeFcisWidget(): Components.FcisWidget {
    return this.fcisWidget?.nativeElement;
  }

  public ngAfterViewInit(): void {
    const observeCalculationRelevantParams = (): Observable<
      [
        Vehicle,
        (VehicleComponent | SelectablesResponseVehicleComponent)[],
        OptionalOnlineOrFinancialCode,
        RatesAndSelectionInfo
      ]
    > =>
      combineLatest([
        // Casting to Vehicle is done as the new generated models can be used
        this.store.select(selectConfigurationVehicle).pipe(filterTruthy()),
        this.store.select(selectSelectedComponents).pipe(filterTruthy()),
        // during configuration an online code can be generated (first time or renewal) or reset (when selecting or deselecting something (aka invalidation)).
        // A online code needs to trigger a new calculation or order to have the relationship between the OC and the calculation
        // so that a returning user - when jumping in via online code - still has the same input and result
        this.store.select(selectOnlineOrFinancialCode),
        // during configuration new selectables might come up. if rates are active we need to trigger calculation again in order to get rates for those new components
        this.store.pipe(selectShowRatesIsActiveAndSelectablesHasChanged)
      ]).pipe(
        // we debounce a little because when we e.g. change a selection we get several emit within a very short time (since we have seveal different observables)
        // with the debounce we save unnecessary OCAPI requests
        // possible improvement: have this in one selector to just have one emit
        // TODO: investigate if this hardcoded delay is really/still necessary
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        debounceTime(200)
      );

    // once initialized (only once!), just do update / retrigger calculation if changes on online code or vehicle
    const observeChangesToRetriggerCalculation = (): void => {
      const triggerCalculation = (
        vehicle: Vehicle,
        selectableComponents: VehicleComponentCollection | undefined,
        selectedComponents: (VehicleComponent | SelectablesResponseVehicleComponent)[],
        onlineOrFinancialCode: OptionalOnlineOrFinancialCode
      ): void => {
        combineLatest([
          this.financeService.getParameter$(
            this.financeService.buildVehicleObject(
              vehicle,
              selectableComponents,
              selectedComponents,
              onlineOrFinancialCode
            ),
            vehicle.vehicleId
          ),
          this.store.select(selectIsFcisInitialized).pipe(
            // ℹ️ calling this function (changeVehicle) before the widget got initialized will lead to errors (can be observed in the console)
            filterTruthy()
          )
        ])
          .pipe(first())
          .subscribe(([parameters]) => {
            this.nativeFcisWidget.parameters = parameters;
          });
      };

      observeCalculationRelevantParams()
        .pipe(
          // ℹ️ intentionally leave the stream open until object got destroyed
          // ℹ️ distinctUntilChanged because we just want to trigger calculation requests if prices got changed (no needless request)
          distinctUntilChanged(([prevVehicle, , prevOnlineCode], [currVehicle, , currOnlineCode, currRateInfo]) => {
            const samePrice =
              prevVehicle.priceInformation?.configurationPrice?.price ===
              currVehicle.priceInformation?.configurationPrice?.price;
            const sameOnlineCode = prevOnlineCode === currOnlineCode;

            if (currRateInfo.showRates) {
              return samePrice && sameOnlineCode && currRateInfo.selectablesHasChanged;
            }
            return samePrice && sameOnlineCode;
          }),
          withLatestFrom(this.store.select(selectConfigurationSelectableComponentsOrUndefined)),
          tap(() => {
            this.store.dispatch(financingFetchFcisVersion({ fcisVersion: this.window?.fcisConfig?.version }));
          }),
          takeUntilDestroyed(appInject(DestroyRef))
        )
        .subscribe(([[vehicle, selectedComponents, onlineCode], selectableComponentsOrUndefined]) => {
          triggerCalculation(vehicle, selectableComponentsOrUndefined, selectedComponents, onlineCode);
        });
    };

    const initFinanceCalculator = (
      vehicle: Vehicle,
      selectedComponents: (VehicleComponent | SelectablesResponseVehicleComponent)[],
      onlineOrFinancialCode: OptionalOnlineOrFinancialCode
      // TODO: Fix with MBCC-2545
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): Observable<any> =>
      // NOTE: for some reason (and just for Firefox) we _first_ have to set the environment and _then_ add the additional params.
      // this still gives us (in Firefox) two messages (on PROD): "fcis environment changed to development" and "fcis environment changed to production"
      // but this code order (first enviroment then other params) doesn't lead to the bug anymore that fcis assets from the "test" environment get loaded.
      // edit: In alignment with FCIS, we now first set the parameters followed by the environment
      combineLatest([
        this.store.pipe(selectFcEnvironment),
        this.store.select(selectConfigurationSelectableComponentsOrUndefined)
      ]).pipe(
        first(),
        switchMap(([fcEnvironment, selectableComponentsOrUndefined]) => {
          const fcisVehicleObj = this.financeService.buildVehicleObject(
            vehicle,
            selectableComponentsOrUndefined,
            selectedComponents,
            onlineOrFinancialCode
          );

          return this.financeService
            .getParameter$(fcisVehicleObj, vehicle.vehicleId)
            .pipe(map(params => [fcEnvironment, params]));
        }),
        tap(([fcEnvironment, params]: [FinanceCalculatorEnvironment, IFcisWidgetParameters]) => {
          this.nativeFcisWidget.parameters = params;
          this.nativeFcisWidget.environment = fcEnvironment;
          this.nativeFcisWidget.stretch = true;
        })
      );

    combineLatest([
      observeCalculationRelevantParams().pipe(first()),
      // to make sure the widget is available (although we don't need this value in the further process)
      of(this.nativeFcisWidget).pipe(filterTruthy())
    ])
      .pipe(
        first(),
        tap(() => {
          observeChangesToRetriggerCalculation();
        }),
        switchMap(([[vehicle, selectedComponents, onlineCode]]) =>
          // Required as the timing seems to shift by one tick and thus the vehicle may be undefined
          // maybe we can improve this (and not use timer anymore) by filtering the vehicle?
          timer(0).pipe(map(() => ({ vehicle, selectedComponents, onlineCode })))
        ),
        switchMap(({ vehicle, selectedComponents, onlineCode }) =>
          initFinanceCalculator(vehicle, selectedComponents, onlineCode)
        )
      )
      .subscribe(() => {
        this.store.dispatch(financingUpdateIsFcisInitialized());
      });
  }

  /**
   * Executed on every quote change / calculation result, e.g. when a user changes a setting like milage
   */
  public onFcisCalculationResult(event: CustomEvent<IOneCalcResponseV2>): void {
    const calculationResult: IOneCalcResponseV2 = event.detail;
    this.store.dispatch(financingUpdateFcisCalculationResult({ calculationResult }));
  }

  public onFcisError(event: CustomEvent): void {
    this.loggerService.error('fcis error:', event);
    this.store.dispatch(financingUpdateIsFcisResultAvailable({ isFcisResultAvailable: false }));
    // TODO: close modal and raise a toaster
  }

  /**
   * Executed on button click "Mtl. Rate übernehmen und anzeigen"
   */
  public onFcisButtonClick(): void {
    // on every button click we should send an event to _then_ take over the rate (not on quote change or on exit with X)
    this.store.dispatch(financingUpdateIsFcisRateConfirmed({ isFcisRateConfirmed: true }));
  }
}
